/*
 * wheel-width-picker.js
 *
 * Construction-aware width picker for the wheel PDP. Per panel
 * (front and, in staggered mode, rear), narrows the Width pill set
 * to widths applicable to the currently-selected Diameter using the
 * product.metafields.ew_.wheel_widths_by_diameter metafield.
 *
 * The section (main-product-wheel.liquid) renders every width once
 * with a data-applicable-diameters attribute listing the diameters
 * it fits. This controller toggles aria-disabled on pills so the
 * existing prop-button click handler (and CSS) treat them as inert.
 *
 * Composes with the chassis-fitment narrowing already in the section
 * (applyNarrowingToPanel) — the existing path manipulates `.hidden`;
 * this controller manipulates aria-disabled. The two states layer.
 *
 * Vanilla JS, no dependencies. Compatible with the no-build theme.
 */
(function () {
  'use strict';

  function safeParse(str) {
    try { return JSON.parse(str); } catch (_) { return null; }
  }

  function diameterNumFromPressed(panel) {
    var pressed = panel.querySelector('[data-ew-diameter-pill][aria-checked="true"]');
    if (!pressed) return '';
    return (pressed.getAttribute('data-option-value') || '').replace(/[^\d]/g, '');
  }

  function WheelWidthPicker(rootEl) {
    this.root = rootEl;
    var pickerEl = rootEl.querySelector('[data-ew-width-picker]');
    var raw = pickerEl ? pickerEl.getAttribute('data-widths-by-diameter') : '{}';
    this.widthsByDiameter = safeParse(raw) || {};
    this.panels = Array.prototype.slice.call(rootEl.querySelectorAll('[data-position]'));
  }

  WheelWidthPicker.prototype.init = function () {
    var self = this;
    if (!this.panels.length) return;
    this.panels.forEach(function (panel) { self.bindPanel(panel); });
    /* Initial pass — section has a pre-selected variant on load. */
    this.panels.forEach(function (panel) { self.applyToPanel(panel); });
  };

  WheelWidthPicker.prototype.bindPanel = function (panel) {
    var self = this;
    panel.querySelectorAll('[data-ew-diameter-pill]').forEach(function (btn) {
      /* Existing section handler updates aria-checked in its own listener.
         We schedule re-narrowing for the next tick so both stay in sync. */
      btn.addEventListener('click', function () {
        Promise.resolve().then(function () { self.applyToPanel(panel); });
      });
    });
  };

  WheelWidthPicker.prototype.applyToPanel = function (panel) {
    var dia = diameterNumFromPressed(panel);
    var pillsContainer = panel.querySelector('[data-ew-width-pills]');
    var pills = panel.querySelectorAll('[data-ew-prop-button][data-prop-key="Width"]');
    var statusEl = panel.querySelector('[data-ew-width-status]');

    if (!dia) {
      if (pillsContainer) pillsContainer.setAttribute('data-locked', 'true');
      pills.forEach(function (pill) {
        pill.setAttribute('aria-disabled', 'true');
        pill.disabled = true;
        pill.setAttribute('aria-checked', 'false');
      });
      if (statusEl) statusEl.textContent = 'Choose a diameter first';
      this.clearWidthSelection(panel, true);
      return;
    }

    if (pillsContainer) pillsContainer.setAttribute('data-locked', 'false');
    var anyApplicable = false;
    var clearedActive = false;
    pills.forEach(function (pill) {
      var applicable = (pill.getAttribute('data-applicable-diameters') || '').split(',');
      var ok = applicable.indexOf(dia) !== -1;
      pill.setAttribute('aria-disabled', ok ? 'false' : 'true');
      pill.disabled = !ok;
      if (ok) anyApplicable = true;
      if (!ok && pill.getAttribute('aria-checked') === 'true') {
        pill.setAttribute('aria-checked', 'false');
        clearedActive = true;
      }
    });

    if (statusEl) {
      if (!anyApplicable) {
        statusEl.textContent = 'No widths configured for this diameter';
      } else {
        var current = panel.querySelector('[data-ew-prop-button][data-prop-key="Width"][aria-checked="true"]');
        statusEl.textContent = current ? '' : 'Select your width';
      }
    }

    if (clearedActive) {
      this.clearWidthSelection(panel, false);
    }
  };

  /* Reset the width display label + (if front panel) the fallback hidden input.
     Notifies the section's inline JS via a bubbling custom event so the
     ATC button gates correctly. */
  WheelWidthPicker.prototype.clearWidthSelection = function (panel, becauseNoDiameter) {
    var label = panel.querySelector('[data-ew-width-current]');
    if (label) label.textContent = '— select —';
    var frontPanel = this.root.querySelector('[data-position="front"]');
    if (panel === frontPanel) {
      var fb = this.root.querySelector('[data-ew-prop-input-fallback][data-prop-key="Width"]');
      if (fb) fb.value = '';
    }
    this.root.dispatchEvent(new CustomEvent('ew:width-cleared', {
      bubbles: true,
      detail: { panel: panel, cause: becauseNoDiameter ? 'no-diameter' : 'inapplicable' }
    }));
  };

  function boot() {
    document.querySelectorAll('[data-ew-wheel-pdp]').forEach(function (root) {
      var picker = new WheelWidthPicker(root);
      picker.init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Expose for debugging / programmatic use. */
  window.EWWheelWidthPicker = WheelWidthPicker;
})();
